import { useState, useEffect, useMemo, useCallback } from 'react';

const MIN_QUERY_LENGTH = 2;

// Lowercase and straighten curly apostrophes/quotes (length-preserving)
const normalize = (s) => s.toLowerCase().replace(/[‘’]/g, "'").replace(/[“”]/g, '"');

// Index of the last word whose offset is <= charPos
function wordIndexAt(offsets, charPos) {
  let lo = 0;
  let hi = offsets.length - 1;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (offsets[mid] <= charPos) lo = mid;
    else hi = mid - 1;
  }
  return lo;
}

/**
 * In-transcript search. Matches are case-insensitive substrings of each
 * paragraph's text (so partial words and multi-word phrases work), mapped
 * back to the word spans to highlight.
 *
 * onJump(startMs) is called when the user steps to a match with next/prev.
 */
export default function useTranscriptSearch(groupedUtterances, onJump) {
  const [query, setQuery] = useState('');
  const [currentMatch, setCurrentMatch] = useState(0);

  const matches = useMemo(() => {
    const q = normalize(query.trim().replace(/\s+/g, ' '));
    if (q.length < MIN_QUERY_LENGTH) return [];

    const found = [];
    groupedUtterances.forEach((group, groupIndex) => {
      const words = group.words;
      if (!words.length) return;

      let text = '';
      const offsets = [];
      words.forEach((w, i) => {
        if (i > 0) text += ' ';
        offsets.push(text.length);
        text += normalize(w.text);
      });

      let pos = text.indexOf(q);
      while (pos !== -1) {
        const firstWord = wordIndexAt(offsets, pos);
        const lastWord = wordIndexAt(offsets, pos + q.length - 1);
        found.push({ groupIndex, firstWord, lastWord, start: words[firstWord].start });
        pos = text.indexOf(q, pos + q.length);
      }
    });
    return found;
  }, [query, groupedUtterances]);

  // "groupIndex:wordIndex" -> match index, for O(1) lookup while rendering
  const wordLookup = useMemo(() => {
    const map = new Map();
    matches.forEach((m, matchIndex) => {
      for (let w = m.firstWord; w <= m.lastWord; w++) {
        map.set(`${m.groupIndex}:${w}`, matchIndex);
      }
    });
    return map;
  }, [matches]);

  // New query: start from the first match
  useEffect(() => {
    setCurrentMatch(0);
  }, [query]);

  // Keep the current match in view
  useEffect(() => {
    if (!matches.length) return;
    const frame = requestAnimationFrame(() => {
      document.querySelector('.search-current')?.scrollIntoView({
        block: 'center',
        behavior: 'smooth',
      });
    });
    return () => cancelAnimationFrame(frame);
  }, [currentMatch, matches]);

  const goTo = useCallback(
    (step) => {
      if (!matches.length) return;
      const next = (currentMatch + step + matches.length) % matches.length;
      setCurrentMatch(next);
      onJump?.(matches[next].start);
    },
    [matches, currentMatch, onJump]
  );

  const nextMatch = useCallback(() => goTo(1), [goTo]);
  const prevMatch = useCallback(() => goTo(-1), [goTo]);
  const clearSearch = useCallback(() => setQuery(''), []);

  // CSS class suffix for a word: ' search-current', ' search-match', or ''
  const getWordSearchClass = useCallback(
    (groupIndex, wordIndex) => {
      const matchIndex = wordLookup.get(`${groupIndex}:${wordIndex}`);
      if (matchIndex === undefined) return '';
      return matchIndex === currentMatch ? ' search-current' : ' search-match';
    },
    [wordLookup, currentMatch]
  );

  return {
    query,
    setQuery,
    matchCount: matches.length,
    currentMatch,
    isSearchActive: query.trim().length >= MIN_QUERY_LENGTH,
    nextMatch,
    prevMatch,
    clearSearch,
    getWordSearchClass,
  };
}
